import { Button, Card, Empty, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type History = {
  marks: Array<{ id: string; attendance_date: string; section_id: string; status_code: string; register_status: string }>;
  amendments: Array<{ id: string; original_status: string; requested_status: string; status: string }>;
  leaves: Array<{ id: string; start_date: string; end_date: string; status: string }>;
};

export default function StudentAttendanceHistoryPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['education-attendance-history', id],
    queryFn: () => api.get<History>(`/education/students/${id}/attendance`),
  });
  const history = query.data?.data;
  const markCount = history?.marks?.length ?? 0;

  return (
    <EducationPageChrome
      title={t('attendanceHistory')}
      description={t('attendanceHistoryIntro')}
      tags={<Tag>{markCount} {t('attendanceTitle')}</Tag>}
      extra={<Button onClick={() => navigate('/education/attendance')}>{t('backToAttendance')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" title={t('attendanceTitle')}>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={history?.marks || []}
            pagination={{ pageSize: 15, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            columns={[
              { title: t('attendanceDate'), dataIndex: 'attendance_date', width: 140 },
              { title: t('section'), dataIndex: 'section_id', ellipsis: true },
              {
                title: t('status'),
                dataIndex: 'status_code',
                width: 120,
                render: (value: string) => <Tag>{value}</Tag>,
              },
            ]}
          />
        </Card>
        <Card size="small" title={t('attendanceCorrections')}>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={history?.amendments || []}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            columns={[
              { title: t('originalStatus'), dataIndex: 'original_status' },
              { title: t('requestedStatus'), dataIndex: 'requested_status' },
              {
                title: t('status'),
                dataIndex: 'status',
                render: (value: string) => <Tag>{value}</Tag>,
              },
            ]}
          />
        </Card>
        <Card size="small" title={t('studentLeave')}>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={history?.leaves || []}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            columns={[
              { title: t('startDate'), dataIndex: 'start_date', width: 140 },
              { title: t('endDate'), dataIndex: 'end_date', width: 140 },
              {
                title: t('status'),
                dataIndex: 'status',
                render: (value: string) => <Tag>{value}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
